import React, { useState, useMemo, useEffect } from 'react';
import { 
  Plus, Search, Landmark, Zap, Crown, ShieldCheck, Award, FileText, 
  PieChart, Plane, X, Lock, Activity, RefreshCw 
} from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * 🛰️ SAHELGUARD EMPIRE COMMAND CENTER - V7.0 (D1 DYNAMIC INTEGRATION)
 * Architecture : Moussa Yarbanga | Infrastructure : Yarmotek
 * Ventilation Article 8 : 80% Trésorerie / 15% Agent / 5% Écosystème
 */

const EmpireCommandCenter = ({ onStartSealing, apiUrl }) => {
  // --- ÉTATS CRM & SYSTÈME ---
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState('checking');
  
  // --- ÉTATS UI ---
  const [showMintModal, setShowMintModal] = useState(false);
  const [targetReseller, setTargetReseller] = useState(null);
  const [inputAmount, setInputAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // --- 📡 MOTEUR DE SYNCHRONISATION D1 ---
  const fetchEmpireData = async () => {
    setLoading(true);
    // On utilise l'apiUrl passée par App.js ou celle par défaut
    const baseUri = apiUrl || "https://sahelguard-api.sahelguard.workers.dev";
    
    try {
      // 1. Test de Santé Système
      const healthRes = await fetch(`${baseUri}/api/health`);
      const health = await healthRes.json();
      setSystemStatus(health.status === "ONLINE" ? "SECURE" : "ALERT");

      // 2. Récupération des Agents
      const agentsRes = await fetch(`${baseUri}/api/agents`);
      const data = await agentsRes.json();
      setResellers(data.agents || []);
    } catch (error) {
      console.error("Erreur Synchro Empire:", error);
      setSystemStatus("OFFLINE");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchEmpireData(); 
  }, [apiUrl]);

  // --- 🧮 MOTEUR DE VENTILATION STRICTE (Instruction 13/02/2026) ---
  const currentFlux = useMemo(() => {
    const base = parseInt(inputAmount) || 0;
    return {
      revendeurDirect: base * 0.15,
      travelPoints: Math.floor((base * 0.01) / 100),
      techSupport: base * 0.01,
      bonusPerformance: base * 0.01,
      parrainageTotal: base * 0.02,
      yarmotekTreasury: base * 0.80,
      total: base
    };
  }, [inputAmount]);

  // --- 🔐 EXÉCUTION DU SCELLAGE (API MINT) ---
  const executeRavitaillement = async () => {
    if (!inputAmount || inputAmount <= 0 || !targetReseller) return;
    setIsProcessing(true);
    
    const baseUri = apiUrl || "https://sahelguard-api.sahelguard.workers.dev";
    
    try {
      const response = await fetch(`${baseUri}/api/mint`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agentId: targetReseller.id,
          amount: parseInt(inputAmount),
          timestamp: new Date().toISOString(),
          signature: "SAHELGUARD_EMPIRE_V7"
        })
      });

      const result = await response.json();

      if (result.success) {
        alert(`🏦 FLUX SCELLÉ\nID: ${result.txId}\n80% Trésorerie Yarmotek sécurisés.`);
        fetchEmpireData(); // Rafraîchissement des soldes
        setShowMintModal(false);
        setInputAmount("");
        setTargetReseller(null);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert("❌ ÉCHEC : Serveur Yarmotek injoignable ou erreur de scellage.");
    } finally {
      setIsProcessing(false);
    }
  };

  // --- 📄 EXPORT AUDIT SOUVERAIN (Basé sur Art. 1-8) ---
  const generateEmpireAudit = () => {
    const doc = new jsPDF();
    doc.setFillColor(5, 5, 5); doc.rect(0, 0, 210, 297, 'F');
    doc.setTextColor(16, 185, 129); doc.setFontSize(22);
    doc.text("SAHELGUARD : AUDIT DE VENTILATION V7.0", 15, 25);
    
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text(`Émis le : ${new Date().toLocaleString()}`, 15, 33);

    doc.autoTable({
      startY: 45,
      head: [['Poste de Dépense', 'Répartition %', 'Statut Juridique']],
      body: [
        ['Commission Vendeur Direct', '15%', 'LIBÉRÉ'],
        ['Challenge Voyage (Points)', '1%', 'SCELLÉ'],
        ['Support Tech (Infrastructure)', '1%', 'SCELLÉ'],
        ['Bonus Performance', '1%', 'DISTRIBUÉ'],
        ['Cascade Gouverneur (Parrain)', '2%', 'SCELLÉ'],
        ['Réserve Empire (Art. 8)', '80%', 'SCELLÉ SOUVERAIN'],
      ],
      theme: 'grid',
      styles: { fillColor: [15, 15, 15], textColor: [255, 255, 255], fontStyle: 'bold' },
      headStyles: { fillColor: [16, 185, 129], textColor: [0, 0, 0] }
    });

    doc.setTextColor(16, 185, 129);
    doc.text("CERTIFICATION : SCELLAGE CONFORME AUX ARTICLES 1 À 8", 15, doc.lastAutoTable.finalY + 20);
    doc.save(`SG_AUDIT_SOUVERAIN_${Date.now()}.pdf`);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 md:p-12 font-sans pt-24">
      <div className="max-w-[1600px] mx-auto">
        
        {/* HEADER */}
        <header className="flex flex-col xl:flex-row justify-between items-center mb-16 border-b border-white/5 pb-12 gap-8">
          <div className="flex items-center gap-6">
            <div className="p-4 bg-emerald-500 rounded-3xl shadow-[0_0_50px_rgba(16,185,129,0.3)]">
              <ShieldCheck size={35} className="text-black" />
            </div>
            <div>
              <h1 className="text-5xl font-black italic uppercase tracking-tighter">Empire Center</h1>
              <div className="flex items-center gap-3 mt-2">
                <span className={`flex items-center gap-1.5 text-[9px] font-bold px-3 py-1 rounded-full border ${systemStatus === 'SECURE' ? 'text-emerald-500 border-emerald-500/20' : 'text-red-500 border-red-500/20'}`}>
                  <Activity size={10} /> {systemStatus === 'SECURE' ? 'SYSTEM SECURE' : 'CONNECTION ALERT'}
                </span>
                <span className="text-[9px] font-black opacity-30 uppercase tracking-widest">v7.0 Stable</span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            <button onClick={() => onStartSealing()} className="px-8 py-4 bg-emerald-500 text-black rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/20">
              <Plus size={16} /> Nouveau Flux
            </button>
            <button onClick={generateEmpireAudit} className="px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-white/10 flex items-center gap-2 transition-all">
              <FileText size={14} /> Audit de Ventilation
            </button>
          </div>
        </header>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 opacity-50">
            <RefreshCw className="animate-spin mb-4" size={40} />
            <p className="text-[10px] font-black uppercase tracking-[0.5em]">Synchronisation D1 Bank...</p>
          </div>
        ) : (
          <div className="grid xl:grid-cols-4 gap-12">
            <div className="xl:col-span-3 space-y-12">
              <div className="grid md:grid-cols-2 2xl:grid-cols-3 gap-8">
                {resellers.length > 0 ? resellers.map(r => (
                  <div key={r.id} className="bg-white/5 border border-white/10 p-10 rounded-[3.5rem] relative overflow-hidden group hover:border-emerald-500/30 transition-all">
                    <div className="flex justify-between mb-8 relative z-10">
                      <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center border border-white/10 group-hover:bg-emerald-500 group-hover:text-black transition-all">
                        <Crown size={24}/>
                      </div>
                      <span className="text-[10px] font-black text-emerald-500 bg-emerald-500/10 px-5 py-2 rounded-full uppercase italic tracking-widest">{r.grade || 'Agent'}</span>
                    </div>
                    
                    <div className="relative z-10">
                      <h3 className="text-3xl font-black italic uppercase tracking-tighter mb-1">{r.name}</h3>
                      <p className="text-[10px] text-slate-500 font-mono mb-8 uppercase tracking-widest">{r.city || 'Sahel Region'}</p>
                      
                      <div className="grid grid-cols-2 gap-4 mb-10">
                        <div className="bg-black/40 p-5 rounded-3xl border border-white/5 text-center">
                          <p className="text-[8px] font-black text-slate-600 uppercase mb-2">Commission Libérée</p>
                          <p className="text-2xl font-black text-emerald-500 italic">{r.balance?.toLocaleString()} <span className="text-[10px]">CFA</span></p>
                        </div>
                        <div className="bg-black/40 p-5 rounded-3xl border border-white/5 text-center">
                          <p className="text-[8px] font-black text-slate-600 uppercase mb-2">Voyage Points</p>
                          <p className="text-2xl font-black text-blue-400 italic">{r.points || 0} <span className="text-[10px]">PTS</span></p>
                        </div>
                      </div>

                      <button onClick={() => { setTargetReseller(r); setShowMintModal(true); }} className="w-full py-5 bg-white text-black rounded-3xl font-black uppercase text-[11px] flex items-center justify-center gap-3 hover:bg-emerald-500 transition-all active:scale-95">
                        <Zap size={18} fill="currentColor" /> Ravitailler Agent
                      </button>
                    </div>
                    <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-emerald-500/5 rounded-full blur-3xl" />
                  </div>
                )) : (
                  <div className="col-span-full py-20 text-center opacity-30 border-2 border-dashed border-white/10 rounded-[3rem]">
                    <p className="text-sm font-black uppercase tracking-[0.3em]">Aucun agent scellé dans la base D1</p>
                  </div>
                )}
              </div>

              <div className="bg-white/[0.02] border border-white/5 rounded-[4rem] p-12">
                 <h2 className="text-2xl font-black italic uppercase tracking-tighter mb-12 flex items-center gap-4">
                   <PieChart className="text-emerald-500" size={28} /> Répartition des Flux (Art. 8)
                 </h2>
                 <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-6 text-center">
                   <div className="p-4 bg-white/5 rounded-2xl border border-white/5"><p className="text-emerald-500 font-black">15%</p><p className="text-[8px] uppercase">Agent</p></div>
                   <div className="p-4 bg-white/5 rounded-2xl border border-white/5"><p className="text-blue-400 font-black">1%</p><p className="text-[8px] uppercase">Voyage</p></div>
                   <div className="p-4 bg-white/5 rounded-2xl border border-white/5"><p className="text-slate-400 font-black">1%</p><p className="text-[8px] uppercase">Support</p></div>
                   <div className="p-4 bg-white/5 rounded-2xl border border-white/5"><p className="text-indigo-400 font-black">1%</p><p className="text-[8px] uppercase">Bonus</p></div>
                   <div className="p-4 bg-white/5 rounded-2xl border border-white/5"><p className="text-orange-400 font-black">2%</p><p className="text-[8px] uppercase">Cascade</p></div>
                   <div className="p-4 bg-emerald-500 text-black rounded-2xl shadow-lg shadow-emerald-500/10"><p className="font-black">80%</p><p className="text-[8px] uppercase font-bold">Empire</p></div>
                 </div>
              </div>
            </div>

            <aside className="space-y-8">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-900 p-10 rounded-[3.5rem] relative overflow-hidden shadow-2xl">
                 <Plane size={120} className="absolute -right-10 -bottom-10 opacity-20 rotate-12" />
                 <h4 className="text-2xl font-black italic uppercase tracking-tighter mb-4 relative z-10">Challenge Voyage</h4>
                 <p className="text-[10px] font-bold uppercase opacity-60 mb-8 tracking-widest italic relative z-10">Target: 1000 Pts</p>
                 <div className="space-y-4 relative z-10 text-[10px] font-black uppercase">
                   <div className="flex justify-between bg-black/20 p-3 rounded-xl"><span>Chine / Inde</span><span className="text-blue-200">1000 Pts</span></div>
                   <div className="flex justify-between bg-black/20 p-3 rounded-xl"><span>Dubaï / Maroc</span><span className="text-blue-200">500 Pts</span></div>
                 </div>
              </div>

              <div className="bg-[#080808] border border-white/5 rounded-[3rem] p-8 text-center border-t-emerald-500/30">
                 <Award size={48} className="mx-auto text-emerald-500 mb-4" />
                 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Trésorerie Centrale</p>
                 <p className="text-4xl font-black italic tracking-tighter">80% SCELLÉ</p>
                 <p className="text-[8px] font-bold text-emerald-500 mt-2 uppercase">Article 8 Appliqué</p>
              </div>
            </aside>
          </div>
        )}
      </div>

      {/* MODAL MINT / SCELLAGE FLUX */}
      {showMintModal && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 backdrop-blur-3xl bg-black/90 animate-in fade-in duration-300">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-xl rounded-[4rem] p-12 relative shadow-[0_0_100px_rgba(16,185,129,0.1)]">
            <button onClick={() => setShowMintModal(false)} className="absolute top-10 right-10 text-slate-500 hover:text-white transition-colors"><X size={32}/></button>
            <div className="text-center mb-10">
              <h2 className="text-4xl font-black italic uppercase tracking-tighter">Injection de Flux</h2>
              <p className="text-[10px] font-black text-emerald-500 uppercase mt-2 tracking-[0.3em]">{targetReseller ? targetReseller.name : "Sélectionner un Agent"}</p>
            </div>
            
            <div className="space-y-8">
              <div className="relative">
                <input 
                  type="number" 
                  value={inputAmount} 
                  onChange={(e) => setInputAmount(e.target.value)}
                  placeholder="0 CFA"
                  className="w-full bg-black border-2 border-white/10 rounded-[2.5rem] py-8 text-5xl font-black text-emerald-500 text-center outline-none focus:border-emerald-500 transition-all placeholder:text-white/5"
                />
                <Landmark className="absolute right-8 top-1/2 -translate-y-1/2 text-white/5" size={40} />
              </div>

              <div className="p-8 bg-white/[0.02] border border-white/5 rounded-[2.5rem] space-y-4 text-[10px] font-black uppercase">
                <div className="flex justify-between text-emerald-500">
                  <span>Commission Agent (15%)</span>
                  <span>+{currentFlux.revendeurDirect.toLocaleString()} CFA</span>
                </div>
                <div className="flex justify-between text-blue-400">
                  <span>Challenge Voyage (1%)</span>
                  <span>+{currentFlux.travelPoints} PTS</span>
                </div>
                <div className="flex justify-between text-indigo-400">
                  <span>Tech & Bonus (3%)</span>
                  <span>+{(currentFlux.techSupport + currentFlux.bonusPerformance + currentFlux.parrainageTotal).toLocaleString()} CFA</span>
                </div>
                <div className="h-px bg-white/5 my-2" />
                <div className="flex justify-between text-white text-xl">
                  <span className="italic">SOUVERAIN (80%)</span>
                  <span className="text-emerald-500">{currentFlux.yarmotekTreasury.toLocaleString()} CFA</span>
                </div>
              </div>

              <button 
                onClick={executeRavitaillement} 
                disabled={isProcessing || !inputAmount || !targetReseller}
                className={`w-full py-8 rounded-[2.5rem] font-black uppercase tracking-widest text-sm flex items-center justify-center gap-4 transition-all shadow-xl ${isProcessing ? 'bg-slate-800 cursor-not-allowed' : 'bg-emerald-500 text-black hover:bg-white hover:scale-105 active:scale-95 shadow-emerald-500/20'}`}
              >
                {isProcessing ? "Scellage en cours..." : "Valider le Scellage Souverain"} <Lock size={18} />
              </button>
              
              <p className="text-[8px] text-center text-slate-500 font-bold uppercase tracking-widest">Cette opération sera enregistrée dans l'historique juridique de l'Empire.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpireCommandCenter;